import React, { useEffect, useRef, useState } from "react";
import { useClients } from "@/context/ClientContext";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import MediaLightbox from "@/components/MediaLightbox";
import api, { apiError, API } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Upload, Film, Image as ImageIcon, FolderOpen, Trash2, Search, Cloud, CloudUpload } from "lucide-react";

const ASPECT = { "9:16": "aspect-[9/16]", "4:5": "aspect-[4/5]", "1:1": "aspect-square", "16:9": "aspect-video" };

const thumbUrl = (a) => a.cover_url || (a.media_type === "image" && a.stored_path ? `${API}/media/file/${a.id}` : null);

export default function MediaLibrary() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const readOnly = Boolean(user?.review_access?.read_only);
  const { current } = useClients();
  const [assets, setAssets] = useState([]);
  const [q, setQ] = useState("");
  const [fmt, setFmt] = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [r2, setR2] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const previewTrigger = useRef(null);
  const fileRef = useRef();
  const images = assets.filter(a => a.media_type === "image");

  useEffect(() => { setPreviewId(null); }, [current?.id, q, fmt]);

  const loadR2 = () => { if (current) api.get(`/media/r2-status?client_id=${current.id}`).then(({ data }) => setR2(data)).catch(() => {}); };
  const load = () => {
    if (!current) return;
    const p = new URLSearchParams({ client_id: current.id });
    if (q) p.append("q", q);
    if (fmt) p.append("fmt", fmt);
    api.get(`/media/assets?${p.toString()}`).then(({ data }) => setAssets(data));
  };
  useEffect(() => { load(); loadR2(); }, [current, q, fmt]);

  const bufferSelected = async () => {
    if (!selected.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/media/buffer", { asset_ids: selected });
      toast.success(`${data.buffered}/${selected.length} Asset(s) nach R2 gepuffert`);
      setSelected([]); load(); loadR2();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const upload = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    fd.append("client_id", current.id);
    [...files].forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post("/media/assets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.count} Asset(s) hochgeladen`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (id) => { await api.delete(`/media/assets/${id}`); load(); toast.success("Asset gelöscht"); };

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleAll = () => setSelected((s) => s.length === assets.length ? [] : assets.map((a) => a.id));
  const deleteSelected = async () => {
    if (!selected.length || !window.confirm(`${selected.length} Assets wirklich löschen?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/media/assets/bulk-delete", { client_id: current.id, ids: selected });
      toast.success(`${data.deleted} Assets gelöscht`);
      setSelected([]); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!current) return <div className="text-slate-400">Kein Mandant ausgewählt.</div>;

  return (
    <div>
      <PageHeader overline={current.name} title="Medienbibliothek" subtitle="Assets mit automatischer Format- & Serien-Erkennung. Dateinamenmuster wie A_(1)_9x16.mp4 werden erkannt."
        actions={
          <>
            <input ref={fileRef} type="file" multiple className="hidden" data-testid="media-file-input" onChange={(e) => upload(e.target.files)} />
            <Button disabled={readOnly} data-testid="media-upload-btn" onClick={() => fileRef.current?.click()} className="rounded-full bg-[#0A0A0A] hover:bg-[#212529] font-semibold"><Upload size={15} className="mr-1.5" /> Assets hochladen</Button>
          </>
        } />

      <div className="flex items-center gap-2 mb-4">
        <div className="relative">
          <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input data-testid="media-search" placeholder="Suche nach Dateiname…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 w-64 h-9 bg-white" />
        </div>
        <Select value={fmt || "__all__"} onValueChange={(v) => setFmt(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-36 h-9 bg-white text-sm" data-testid="media-format-filter"><SelectValue placeholder="Format" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Alle Formate</SelectItem>
            {["9:16", "1:1", "4:5", "16:9"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-3">
          {assets.length > 0 && (
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <Checkbox checked={selected.length === assets.length && assets.length > 0} onCheckedChange={toggleAll} data-testid="media-select-all" />
              <span className="text-slate-500">{selected.length > 0 ? `${selected.length} markiert` : "Alle markieren"}</span>
            </label>
          )}
          {selected.length > 0 && (
            <Button size="sm" variant="outline" disabled={busy || readOnly} onClick={bufferSelected} data-testid="media-buffer-selected" className="rounded-full h-8"><CloudUpload size={14} className="mr-1.5" /> Nach R2 puffern</Button>
          )}
          {selected.length > 0 && (
            <Button size="sm" variant="destructive" disabled={busy || readOnly} onClick={deleteSelected} data-testid="media-delete-selected" className="rounded-full h-8"><Trash2 size={14} className="mr-1.5" /> {selected.length} löschen</Button>
          )}
          {r2?.configured && (
            <span data-testid="r2-status-chip" className="inline-flex items-center gap-1.5 text-xs font-medium text-sky-700 bg-sky-50 px-2.5 py-1 rounded-full"><Cloud size={13} /> R2 {r2.assets_buffered}/{r2.assets_total}</span>
          )}
          <span className="text-sm text-slate-400 font-mono">{assets.length} Assets</span>
        </div>
      </div>

      {assets.length === 0 ? (
        <EmptyState icon={FolderOpen} title="Keine Assets" hint="Lade Videos/Bilder hoch – das Format wird automatisch aus dem Dateinamen erkannt." />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {assets.map((a) => (
            <Card key={a.id} data-testid={`asset-card-${a.id}`} className="rounded-md border-slate-200 shadow-none overflow-hidden group hover:-translate-y-[1px] transition-transform duration-150">
              <div className={`relative bg-slate-900 ${ASPECT[a.format] || "aspect-video"} overflow-hidden flex items-center justify-center`}>
                {thumbUrl(a) ? (
                  <img src={thumbUrl(a)} alt={a.alt_text || a.filename} referrerPolicy="no-referrer" loading="lazy"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling && (e.currentTarget.nextSibling.style.display = "flex"); }} />
                ) : null}
                <div className="absolute inset-0 flex-col items-center justify-center text-slate-500" style={{ display: thumbUrl(a) ? "none" : "flex" }}>
                  {a.media_type === "video" ? <Film size={30} /> : <ImageIcon size={30} />}
                  <span className="text-[10px] mt-1 text-slate-400">{a.media_type === "video" ? "Video" : "Datei"}</span>
                </div>
                {a.media_type === "image" && (
                  <button type="button" data-testid={`asset-preview-${a.id}`}
                    aria-label={`${t("Bild groß anzeigen")}: ${a.filename}`} aria-haspopup="dialog"
                    onClick={event => { previewTrigger.current = event.currentTarget; setPreviewId(a.id); }}
                    className="absolute inset-0 cursor-zoom-in focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-white" />
                )}
                <div className="absolute top-2 left-2 flex gap-1 pointer-events-none">
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-white/90 text-slate-800">{a.format || "?"}</span>
                  {a.series && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-[#0A0A0A] text-white">Serie {a.series}</span>}
                  {a.r2_key && <span title="In R2 gepuffert" className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-sky-600 text-white flex items-center gap-0.5" data-testid={`asset-r2-badge-${a.id}`}><Cloud size={9} /> R2</span>}
                </div>
                <div className="absolute top-2 right-2 pointer-events-none">
                  {a.media_type === "video" ? <Film size={16} className="text-white drop-shadow" /> : <ImageIcon size={16} className="text-white drop-shadow" />}
                </div>
                <button disabled={readOnly} data-testid={`asset-delete-${a.id}`} onClick={() => del(a.id)} className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-sm p-1 hover:bg-rose-100"><Trash2 size={13} className="text-rose-600" /></button>
              </div>
              <div className="p-2.5">
                <div className="font-mono text-[11px] truncate text-slate-700" title={a.filename}>{a.filename}</div>
                <div className="text-[10px] text-slate-400 mt-0.5 uppercase">{a.source} · {a.size_bytes ? (a.size_bytes / 1e6).toFixed(1) + " MB" : "—"}</div>
              </div>
            </Card>
          ))}
        </div>
      )}
      <MediaLightbox images={images} assetId={previewId} onNavigate={setPreviewId}
        onClose={() => setPreviewId(null)} triggerRef={previewTrigger} />
    </div>
  );
}
